import { CheckIcon, CloseIcon, DeleteIcon, EditIcon } from "@chakra-ui/icons";
import {
  Box,
  FormLabel,
  HStack,
  IconButton,
  SimpleGrid,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useColorModeValue,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import CustomButton from "../../../components/Common/CustomButton";
import CustomInput from "../../../components/Common/CustomInput";
import Card from "../../../components/card/Card";
import { selectFormData, selectItemsLoading } from "./redux/selector";
import { actions } from "./redux/slice";
import { useState } from "react";

const Items = () => {
  const { t } = useTranslation();
  const textColor = useColorModeValue("navy.700", "white");
  const [sortOrder, setSortOrder] = useState<string>("|");
  const dispatch = useDispatch();
  const form = useSelector(selectFormData);
  const loading = useSelector(selectItemsLoading);
  const [edit, setEdit] = useState<number | null>(null);
  const handleSubmit = () => {
    dispatch(
      actions.doCreateItems({
        callback() {
          dispatch(actions.clearItemsFrom());
        },
      })
    );
  };
  const handleDelete = (i: number) => {
    dispatch(
      actions.doRemoveItems({
        index: i,
      })
    );
  };
  const handleFieldChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = evt.target;
    dispatch(actions.updateFormValue({ key: name, value: value }));
  };

  const startEdit = (index: number) => {
    const row = form.priceItems[index];
    setEdit(index);
    dispatch(
      actions.updateFormValue({ key: "priceItemsForm.min", value: row.min })
    );
    dispatch(
      actions.updateFormValue({ key: "priceItemsForm.max", value: row.max })
    );
    dispatch(
      actions.updateFormValue({ key: "priceItemsForm.price", value: row.price })
    );
  };
  const saveEdit = (index: number) => {
    dispatch(
      actions.doUpdateItems({
        index,
        data: {
          min: Number(form?.priceItemsForm?.min),
          max: Number(form?.priceItemsForm?.max),
          price: Number(form?.priceItemsForm?.price),
        },
        callback: () => {
          dispatch(actions.clearItemsFrom());
          setEdit(null);
        },
      })
    );
  };

  const renderSortingIcon = (column: string) => {
    if (edit !== null || !sortOrder) return null;
    const [key, order] = sortOrder.split("|");
    if (column !== key) return null;
    if (column === key && order === "asc") {
      return " 🔼";
    }
    if (column === key && order === "desc") {
      return " 🔽";
    }
    return null;
  };

  const sortOrderUpdate = (a: string) => {
  

    const [key, order] = sortOrder.split("|");
    if (key !== a) {
      setSortOrder(`${a}|asc`);
    } else {
      setSortOrder(`${a}|${order === "asc" ? "desc" : "asc"}`);
    }
  };

  return (
    <Card my="20px" p="20px" flexDirection="column">
      <FormLabel
        display="flex"
        ms="4px"
        fontSize="lg"
        fontWeight="500"
        color={textColor}
        mb="8px"
      >
        Items
      </FormLabel>
      <SimpleGrid columns={[1, 1, 2, 3, 4]} spacingX={5} alignItems="center">
        <CustomInput
          name={"priceItemsForm.min"}
          value={form?.priceItemsForm?.min?.toString()}
          handleChange={handleFieldChange}
          placeholder={t("shipping.form.enterMinimumQuantity")}
          title={t("shipping.form.minimumQuantity")}
          type="number"
        />
        <CustomInput
          name={"priceItemsForm.max"}
          value={form?.priceItemsForm?.max?.toString()}
          handleChange={handleFieldChange}
          placeholder={t("shipping.form.enterMaximumQuantity")}
          title={t("shipping.form.maximumQuantity")}
          type="number"
        />
        <CustomInput
          name={"priceItemsForm.price"}
          value={form?.priceItemsForm?.price?.toString()}
          handleChange={handleFieldChange}
          placeholder={t("shipping.form.enterPrice")}
          title={t("shipping.form.price")}
          type="number"
        />

        <Box mt={8}>
          <CustomButton
            handleClick={handleSubmit}
            title={t("common.button.add")}
            loading={loading}
            sx={{ marginLeft: "20px", maxWidth: "100px" }}
            size="sm"
            disabled={form?.priceItemsForm?.price == 0 || edit !== null}
          />
        </Box>
      </SimpleGrid>
      {form?.priceItems.filter((x) => !x?.remove).length > 0 && (
        <Card>
          <Table variant="simple" maxWidth="50%">
            <Thead bg="gray.100">
              <Tr>
                <Th width="100px">{t("common.list.sNo")}</Th>
                <Th onClick={() => sortOrderUpdate("min")}>
                  {t("shipping.form.min")}
                  {renderSortingIcon("min")}
                </Th>
                <Th onClick={() => sortOrderUpdate("max")}>
                  {t("shipping.form.max")}
                  {renderSortingIcon("max")}
                </Th>
                <Th onClick={() => sortOrderUpdate("price")}>
                  {t("shipping.form.price")}
                  {renderSortingIcon("price")}
                </Th>
                <Th isNumeric width="100px">
                  {t("common.list.actions")}
                </Th>
              </Tr>
            </Thead>
            <Tbody>
              {[...(form?.priceItems || [])].sort((a, b) => {
                const [key, order] = sortOrder.split("|");
                if (key === "min") {
                  return order === "asc" ? a.min - b.min : b.min - a.min;
                }
                if (key === "max") {
                  return order === "asc" ? a.max - b.max : b.max - a.max;
                }
                if (key === "price") {
                  return order === "asc" ? a.price - b.price : b.price - a.price;
                }
                return 0;
              }).map((row, index) => {
                if (row?.remove) return null;
                const isEditing = edit === index;
                return (
                  <Tr
                    key={index}
                    _hover={{ bg: "gray.50" }}
                    sx={{ alignItems: "baseline" }}
                  >
                    <Td>
                      <Text fontWeight="semibold">{index + 1}</Text>
                    </Td>
                    <Td pr={3}>
                      {isEditing ? (
                        <CustomInput
                          name="priceItemsForm.min"
                          value={form?.priceItemsForm?.min?.toString()}
                          handleChange={handleFieldChange}
                          type="number"
                          placeholder={t("shipping.form.enterMinimumQuantity")}
                          title=""
                          sx={{ marginBottom: 0 }}
                        />
                      ) : (
                        <Text fontSize="sm" color="gray.500">
                          {row?.min}
                        </Text>
                      )}
                    </Td>
                    <Td pr={3}>
                      {isEditing ? (
                        <CustomInput
                          name="priceItemsForm.max"
                          value={form?.priceItemsForm?.max?.toString()}
                          handleChange={handleFieldChange}
                          type="number"
                          placeholder={t("shipping.form.enterMaximumQuantity")}
                          title=""
                          sx={{ marginBottom: 0 }}
                        />
                      ) : (
                        <Text fontSize="sm" color="gray.500">
                          {row?.max}
                        </Text>
                      )}
                    </Td>
                    <Td pr={3}>
                      {isEditing ? (
                        <CustomInput
                          name="priceItemsForm.price"
                          value={form?.priceItemsForm?.price?.toString()}
                          handleChange={handleFieldChange}
                          type="number"
                          placeholder={t("shipping.form.enterPrice")}
                          title=""
                          sx={{ marginBottom: 0 }}
                        />
                      ) : (
                        <Text fontSize="sm" color="gray.500">
                          {row?.price}
                        </Text>
                      )}
                    </Td>
                    <Td isNumeric>
                      {isEditing ? (
                        <HStack>
                          <Tooltip label={t("common.button.cancel")}>
                            <IconButton
                              aria-label="cancel"
                              icon={<CloseIcon />}
                              onClick={() => {
                                setEdit(null);
                                dispatch(actions.clearItemsFrom());
                              }}
                              size="xs"
                            />
                          </Tooltip>
                          <Tooltip label={t("common.button.save")}>
                            <IconButton
                              aria-label="save"
                              icon={<CheckIcon />}
                              onClick={() => saveEdit(index)}
                              colorScheme="green"
                              size="xs"
                            />
                          </Tooltip>
                        </HStack>
                      ) : (
                        <HStack>
                          <Tooltip label={`${t("common.list.edit")}`}>
                            <IconButton
                              aria-label="Edit"
                              icon={<EditIcon />}
                              onClick={() => startEdit(index)}
                            />
                          </Tooltip>
                          <Tooltip
                            label={`${t("common.list.delete")}`}
                            hasArrow
                          >
                            <IconButton
                              icon={<DeleteIcon />}
                              size="sm"
                              colorScheme="red"
                              variant="ghost"
                              onClick={() => handleDelete(index)}
                              aria-label="Delete FAQ"
                            />
                          </Tooltip>
                        </HStack>
                      )}
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        </Card>
      )}
    </Card>
  );
};

export default Items;
